import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { RequestService } from '@core/services/request/request.service';
import { WorkflowService } from '@core/services/workflow/workflow.service';
import { XAlertService } from '@core/services/x-alert.service';
import {
  distinctUntilChanged,
  EMPTY,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  RequestWorkflowsData,
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS,
  SyncWorkflowRemoveFileResponse,
  SyncWorkflowUploadFileResponse,
  WorkflowFormSubmissionFileExternal,
  WorkflowSubmissionRequestFile,
} from '@workflows/types';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { WorkflowHttpErrorsService } from '@core/services/workflow/workflow-http-errors.service';
import {
  getPhotoQueueTargetKey,
  PhotoQueueService,
} from '@core/services/workflow/photo-queue.service';
import { isStalePhotoQueueTask } from './photo-queue-error';
import {
  PhotoQueueBlobRecord,
  PhotoQueueEntry,
  PhotoQueueError,
  PhotoQueueTarget,
  PhotoRemoveQueueTask,
  PhotoUploadQueueTask,
} from './input-photos-queue.types';
import { PhotoQueueEvent } from './photo-queue.types';
import { FileItem, PhotoStatus } from './input-photos.types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { WorkflowsTranslations } from '@core/types';

export type { FileItem, PhotoStatus, Settings } from './input-photos.types';

/** Статусы фото, которые входят в значение поля (на сервере или ждут загрузки) */
const FIELD_VALUE_STATUSES: ReadonlySet<PhotoStatus> = new Set<PhotoStatus>([
  'new',
  'uploading',
  'queued_upload',
  'completed',
  'remove_error',
]);

@Injectable({
  providedIn: 'root',
})
export class InputPhotosService {
  private readonly requestService = inject(RequestService);
  private readonly workflowService = inject(WorkflowService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly workflowHttpErrorsService = inject(
    WorkflowHttpErrorsService,
  );
  private readonly photoQueueService = inject(PhotoQueueService);
  private readonly destroyRef = inject(DestroyRef);

  readonly requestNumber = computed(
    () => this.requestService.$request().requestNumber,
  );
  readonly photoFiles = signal<FileItem[]>([]);
  private readonly files$ = toObservable(this.photoFiles);

  readonly photoPreviewKey = signal<string | null>(null);
  private hydrationGeneration = 0;

  constructor() {
    this.photoQueueService.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        void this.handleQueueEvent(event);
      });

    // Гидратация фото привязана к заявке: при смене заявки/пользователя (данные
    // сбрасываются в null) состояние очищается и собирается заново; результат
    // гидратации предыдущей заявки, завершившейся позже, не применяется
    toObservable(this.workflowService.$requestsWorkflowData)
      .pipe(
        map(data =>
          data
            ? {
                data,
                key:
                  data.requestWorkflowsContext?.requestNumber ??
                  this.requestNumber(),
              }
            : null,
        ),
        distinctUntilChanged(
          (previous, current) => previous?.key === current?.key,
        ),
        switchMap(entry => {
          const generation = ++this.hydrationGeneration;

          if (!entry) {
            this.photoFiles.set([]);

            return EMPTY;
          }

          return from(this.loadPhotoState(entry.data, entry.key)).pipe(
            map(files => ({ files, generation })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ files, generation }) => {
        if (files && generation === this.hydrationGeneration) {
          this.applyHydratedFiles(files);
        }
      });
  }

  addFiles(files: FileItem[]): void {
    if (files.length === 0) {
      return;
    }

    this.photoFiles.update(currentFiles => [...currentFiles, ...files]);

    from(files)
      .pipe(
        mergeMap(file => this.uploadFile(file), 10),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  retryFileUpload(fileItem: FileItem): void {
    if (fileItem.activeTaskId) {
      this.updatePhoto(fileItem.id, {
        status: 'queued_upload',
        lastError: undefined,
      });
      void this.photoQueueService.retry(fileItem.activeTaskId).catch(error => {
        this.handleQueueStorageFailure(fileItem.id, 'upload', error);
      });

      return;
    }

    this.uploadFile(fileItem)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  retryFileRemove(fileItem: FileItem): void {
    if (fileItem.activeTaskId) {
      this.updatePhoto(fileItem.id, {
        status: 'queued_remove',
        lastError: undefined,
      });
      void this.photoQueueService.retry(fileItem.activeTaskId).catch(error => {
        this.handleQueueStorageFailure(fileItem.id, 'remove', error);
      });

      return;
    }

    this.removeUploadedFile(fileItem);
  }

  deleteFile(fileToDelete: FileItem): void {
    if (!fileToDelete.createdSubmissionFileId) {
      void this.cancelLocalFile(fileToDelete);

      return;
    }

    this.removeUploadedFile(fileToDelete);
  }

  dismissRemoveError(fileItem: FileItem): void {
    if (!fileItem.activeTaskId) {
      this.updatePhoto(fileItem.id, {
        status: 'completed',
        lastError: undefined,
      });

      return;
    }

    void this.photoQueueService
      .cancel(fileItem.activeTaskId)
      .then(() => {
        this.updatePhoto(fileItem.id, {
          status: 'completed',
          activeTaskId: undefined,
          lastError: undefined,
        });
      })
      .catch(error => {
        this.handleQueueStorageFailure(fileItem.id, 'remove', error);
      });
  }

  clearPhotoFiles(): void {
    this.photoFiles.set([]);
  }

  /**
   * Фото поля экземпляра формы: (requestNumber, requestWorkflowId, nodeId, formId, fieldKey).
   * Поля с тем же ключом в других формах, регламентах и заявках не учитываются.
   */
  getTargetFiles(target: PhotoQueueTarget): FileItem[] {
    const targetKey = getPhotoQueueTargetKey(target);

    return this.photoFiles().filter(
      file => getPhotoQueueTargetKey(file.settings) === targetKey,
    );
  }

  /**
   * Значение контрола поля: фото на сервере и ожидающие загрузки
   * (без удаляемых и не загрузившихся)
   */
  getTargetValue(target: PhotoQueueTarget): FileItem[] {
    return this.getTargetFiles(target).filter(file =>
      FIELD_VALUE_STATUSES.has(file.status),
    );
  }

  getPendingCountForTarget$(target: PhotoQueueTarget): Observable<number> {
    const targetKey = getPhotoQueueTargetKey(target);

    return this.files$.pipe(
      map(
        files =>
          files.filter(
            file =>
              getPhotoQueueTargetKey(file.settings) === targetKey &&
              this.isPendingSyncStatus(file.status),
          ).length,
      ),
      distinctUntilChanged(),
    );
  }

  getPendingCountForNode$(
    requestWorkflowId: string,
    nodeId: number,
  ): Observable<number> {
    return this.files$.pipe(
      map(
        files =>
          files.filter(
            file =>
              file.settings.workflowId === requestWorkflowId &&
              file.settings.nodeId === nodeId &&
              this.isPendingSyncStatus(file.status),
          ).length,
      ),
      distinctUntilChanged(),
    );
  }

  private uploadFile(
    fileItem: FileItem,
  ): Observable<SyncWorkflowUploadFileResponse | null> {
    const operationId = fileItem.activeTaskId ?? crypto.randomUUID();
    const formData = new FormData();

    if (fileItem.file) {
      formData.append('file', fileItem.file);
    }

    this.updatePhoto(fileItem.id, {
      status: 'uploading',
      activeTaskId: operationId,
      lastError: undefined,
    });

    return this.workflowService
      .syncWorkflowUploadFile(
        fileItem.settings.requestNumber,
        fileItem.settings.workflowId,
        fileItem.settings.nodeId,
        fileItem.settings.formId,
        fileItem.settings.fieldId,
        formData,
        operationId,
      )
      .pipe(
        tap(response => {
          if (response.isOk) {
            this.updatePhoto(fileItem.id, {
              status: 'completed',
              createdSubmissionFileId: response.data.createdSubmissionFileId,
              activeTaskId: undefined,
              lastError: undefined,
            });
            this.addUploadedSubmissionFile(fileItem, response.data);

            return;
          }

          this.updatePhoto(fileItem.id, {
            status: 'upload_error',
            activeTaskId: undefined,
            lastError: this.createQueueError(
              `upload_${response.failureReason}`,
              response.failureReason,
              false,
            ),
          });
        }),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 0) {
            this.workflowHttpErrorsService.showFileUploadHttpError(error);

            return from(this.enqueueUploadTask(fileItem, operationId)).pipe(
              map(() => null),
              catchError((storageError: unknown) => {
                this.handleQueueStorageFailure(
                  fileItem.id,
                  'upload',
                  storageError,
                );

                return of(null);
              }),
            );
          }

          this.updatePhoto(fileItem.id, {
            status: 'upload_error',
            activeTaskId: undefined,
            lastError: this.createHttpQueueError(error),
          });
          if (error instanceof HttpErrorResponse) {
            this.workflowHttpErrorsService.showFileUploadHttpError(error);
          }

          return of(null);
        }),
      );
  }

  private removeUploadedFile(fileItem: FileItem): void {
    if (!fileItem.createdSubmissionFileId) {
      return;
    }

    const operationId = fileItem.activeTaskId ?? crypto.randomUUID();

    this.updatePhoto(fileItem.id, {
      status: 'deleting',
      activeTaskId: operationId,
      lastError: undefined,
    });

    this.workflowService
      .syncWorkflowRemoveFile(
        fileItem.settings.requestNumber,
        fileItem.settings.workflowId,
        fileItem.settings.nodeId,
        fileItem.settings.formId,
        fileItem.settings.fieldId,
        fileItem.createdSubmissionFileId,
        operationId,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (
            response?.isOk ||
            this.isSubmissionFileNotFoundResponse(response)
          ) {
            this.completeFileRemoval(fileItem);

            return;
          }

          this.updatePhoto(fileItem.id, {
            status: 'remove_error',
            activeTaskId: undefined,
            lastError: this.createQueueError(
              `remove_${response?.failureReason ?? 'unknown'}`,
              response?.failureReason ?? 'Unknown remove error',
              false,
            ),
          });
        },
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 0) {
            void this.enqueueRemoveTask(fileItem, operationId).catch(
              storageError => {
                this.handleQueueStorageFailure(
                  fileItem.id,
                  'remove',
                  storageError,
                );
              },
            );

            return;
          }

          this.updatePhoto(fileItem.id, {
            status: 'remove_error',
            activeTaskId: undefined,
            lastError: this.createHttpQueueError(error),
          });
        },
      });
  }

  private async cancelLocalFile(fileItem: FileItem): Promise<void> {
    if (fileItem.activeTaskId) {
      try {
        await this.photoQueueService.cancel(fileItem.activeTaskId);
      } catch (error) {
        this.handleQueueStorageFailure(fileItem.id, 'upload', error);

        return;
      }
    }

    this.removeFileFromPhotosInput(fileItem.id);
  }

  private removeFileFromPhotosInput(photoId: string): void {
    this.photoFiles.update(files => files.filter(file => file.id !== photoId));
  }

  private completeFileRemoval(fileItem: FileItem): void {
    this.removeFileFromPhotosInput(fileItem.id);

    if (fileItem.createdSubmissionFileId) {
      this.workflowService.removeSubmissionFile(
        fileItem.settings.workflowId,
        fileItem.settings.formId,
        fileItem.createdSubmissionFileId,
      );
    }
  }

  private isPendingSyncStatus(status: PhotoStatus): boolean {
    return (
      status === 'uploading' ||
      status === 'queued_upload' ||
      status === 'deleting' ||
      status === 'queued_remove'
    );
  }

  private async enqueueUploadTask(
    fileItem: FileItem,
    operationId: string,
  ): Promise<void> {
    if (!fileItem.file) {
      throw new Error('File is not available for offline upload');
    }

    const blobKey = crypto.randomUUID();
    const blobRecord: PhotoQueueBlobRecord = {
      blobKey,
      fileName: fileItem.file.name,
      mimeType: fileItem.file.type,
      lastModified: fileItem.file.lastModified,
      blob: fileItem.file,
    };
    const task: PhotoUploadQueueTask = {
      schemaVersion: 2,
      id: operationId,
      photoId: fileItem.id,
      operation: 'upload',
      target: fileItem.settings,
      createdAt: Date.now(),
      attemptCount: 0,
      state: 'retry_wait',
      nextAttemptAt: Date.now() + 30_000,
      blobKey,
    };

    await this.photoQueueService.addUploadTask(task, blobRecord);
    this.updatePhoto(fileItem.id, {
      status: 'queued_upload',
      activeTaskId: operationId,
      lastError: undefined,
    });
  }

  private async enqueueRemoveTask(
    fileItem: FileItem,
    operationId: string,
  ): Promise<void> {
    if (!fileItem.createdSubmissionFileId) {
      throw new Error('Submission file id is required for queued removal');
    }

    const task: PhotoRemoveQueueTask = {
      schemaVersion: 2,
      id: operationId,
      photoId: fileItem.id,
      operation: 'remove',
      target: fileItem.settings,
      createdAt: Date.now(),
      attemptCount: 0,
      state: 'retry_wait',
      nextAttemptAt: Date.now() + 30_000,
      createdSubmissionFileId: fileItem.createdSubmissionFileId,
    };

    await this.photoQueueService.addRemoveTask(task);
    this.updatePhoto(fileItem.id, {
      status: 'queued_remove',
      activeTaskId: operationId,
      lastError: undefined,
    });
  }

  /**
   * Собрать состояние фото заявки: серверные файлы + задачи очереди владельца
   */
  private async loadPhotoState(
    workflowData: RequestWorkflowsData,
    requestNumber: string,
  ): Promise<FileItem[] | null> {
    try {
      const allEntries = await this.photoQueueService.getEntries();
      const entries: PhotoQueueEntry[] = [];

      for (const entry of allEntries) {
        // Цель задачи исчезла из схемы регламентов заявки: конфликт без повторов
        if (
          entry.task.target.requestNumber === requestNumber &&
          entry.task.state !== 'succeeded' &&
          !this.isTargetInSchema(workflowData, entry.task.target)
        ) {
          await this.photoQueueService.markStaleTarget(entry.task.id);
          continue;
        }

        if (!isStalePhotoQueueTask(entry.task)) {
          entries.push(entry);
        }
      }

      const serverFiles = this.getAllFilesFromWorkflowSubmissions(
        workflowData,
        requestNumber,
      );
      const mergedFiles = this.mergeQueueEntries(serverFiles, entries);

      for (const entry of entries) {
        if (entry.task.state !== 'succeeded') {
          continue;
        }

        if (entry.task.operation === 'remove') {
          this.workflowService.removeSubmissionFile(
            entry.task.target.workflowId,
            entry.task.target.formId,
            entry.task.createdSubmissionFileId,
          );
        }

        await this.photoQueueService.acknowledgeSuccess(entry.task.id);
      }

      return mergedFiles;
    } catch (error) {
      this.showQueueStorageError(error);

      return null;
    }
  }

  private applyHydratedFiles(mergedFiles: FileItem[]): void {
    this.photoFiles.update(currentFiles => {
      const hydratedPhotoIds = new Set(mergedFiles.map(file => file.id));
      const filesAddedDuringInitialization = currentFiles.filter(
        file => !hydratedPhotoIds.has(file.id),
      );

      return [...mergedFiles, ...filesAddedDuringInitialization];
    });
  }

  /**
   * Поле задачи существует в текущей схеме: регламент заявки, его нода, форма ноды и поле формы
   */
  private isTargetInSchema(
    workflowData: RequestWorkflowsData,
    target: PhotoQueueTarget,
  ): boolean {
    const requestWorkflow = workflowData.requestsWorkflows.find(
      rw => rw.id === target.workflowId,
    );

    if (!requestWorkflow?.nodes.some(node => node.id === target.nodeId)) {
      return false;
    }

    const form = workflowData.workflowNodesForms.find(
      nodeForm =>
        nodeForm.id === target.formId &&
        nodeForm.workflowNodeId === target.nodeId,
    );

    return Boolean(
      form?.schema.groups.some(group =>
        group.fields.some(field => field.key === target.fieldId),
      ),
    );
  }

  private addUploadedSubmissionFile(
    fileItem: FileItem,
    result: {
      createdSubmissionFileId: number;
      uploadedToSubmissionId?: number;
      uploadedFile?: WorkflowSubmissionRequestFile;
    },
  ): void {
    if (!result.uploadedFile || result.uploadedToSubmissionId === undefined) {
      return;
    }

    this.workflowService.addSubmissionFile(
      fileItem.settings.workflowId,
      fileItem.settings.nodeId,
      fileItem.settings.formId,
      {
        id: result.createdSubmissionFileId,
        formSubmissionId: result.uploadedToSubmissionId,
        fieldKey: fileItem.settings.fieldId,
        logisticRequestFileId: result.uploadedFile.id,
        logisticRequestFile: result.uploadedFile,
        createdAt: new Date().toISOString(),
        createdId: 0,
      },
    );
  }

  private getAllFilesFromWorkflowSubmissions(
    workflowData: RequestWorkflowsData,
    requestNumber: string,
  ): FileItem[] {
    return workflowData.submissions.reduce((files, submission) => {
      if (!Array.isArray(submission.files)) {
        return files;
      }

      // Форма отсутствует в текущей схеме (устаревшие данные) — её файлы не показываем
      const nodeId = this.workflowService.getNodeIdByFormId(
        submission.workflowNodeFormId,
      );

      if (nodeId === null) {
        return files;
      }

      const submissionFiles = submission.files.map(
        (file: WorkflowFormSubmissionFileExternal): FileItem => ({
          id: `server:${file.id}`,
          path: file.logisticRequestFile?.path || '',
          name: file.logisticRequestFile?.filename || '',
          createdSubmissionFileId: file.id,
          status: 'completed',
          settings: {
            requestNumber,
            workflowId: submission.requestWorkflowId,
            nodeId,
            formId: submission.workflowNodeFormId,
            fieldId: file.fieldKey,
          },
        }),
      );

      return [...files, ...submissionFiles];
    }, [] as FileItem[]);
  }

  private mergeQueueEntries(
    serverFiles: FileItem[],
    entries: PhotoQueueEntry[],
  ): FileItem[] {
    let mergedFiles = [...serverFiles];

    for (const { task, blobRecord } of entries) {
      if (task.operation === 'upload') {
        const uploadedFileId = task.result?.createdSubmissionFileId;

        // Загрузка из очереди уже видна среди серверных файлов — не дублируем
        if (
          !blobRecord ||
          (task.state === 'succeeded' &&
            serverFiles.some(
              file => file.createdSubmissionFileId === uploadedFileId,
            ))
        ) {
          continue;
        }

        const status = this.getUploadStatusForTask(task.state);
        const queuedFile: FileItem = {
          id: task.photoId,
          name: blobRecord.fileName,
          file: new File([blobRecord.blob], blobRecord.fileName, {
            type: blobRecord.mimeType,
            lastModified: blobRecord.lastModified,
          }),
          status,
          settings: task.target,
          activeTaskId: task.state === 'succeeded' ? undefined : task.id,
          createdSubmissionFileId:
            task.state === 'succeeded'
              ? task.result?.createdSubmissionFileId
              : undefined,
          lastError: task.lastError,
        };
        const existingIndex = mergedFiles.findIndex(
          file => file.id === task.photoId,
        );

        mergedFiles =
          existingIndex === -1
            ? [...mergedFiles, queuedFile]
            : mergedFiles.map((file, index) =>
                index === existingIndex ? queuedFile : file,
              );

        continue;
      }

      const existingFile = mergedFiles.find(
        file => file.createdSubmissionFileId === task.createdSubmissionFileId,
      );

      if (!existingFile) {
        continue;
      }

      if (task.state === 'succeeded') {
        mergedFiles = mergedFiles.filter(file => file.id !== existingFile.id);

        continue;
      }

      mergedFiles = mergedFiles.map(file =>
        file.id === existingFile.id
          ? {
              ...file,
              status: this.getRemoveStatusForTask(task.state),
              activeTaskId: task.id,
              lastError: task.lastError,
            }
          : file,
      );
    }

    return mergedFiles;
  }

  private getUploadStatusForTask(
    state: PhotoUploadQueueTask['state'],
  ): PhotoStatus {
    if (state === 'failed') {
      return 'upload_error';
    }

    if (state === 'succeeded') {
      return 'completed';
    }

    return 'queued_upload';
  }

  private getRemoveStatusForTask(
    state: PhotoRemoveQueueTask['state'],
  ): PhotoStatus {
    return state === 'failed' ? 'remove_error' : 'queued_remove';
  }

  private async handleQueueEvent(event: PhotoQueueEvent): Promise<void> {
    if (event.type === 'started') {
      this.updatePhoto(event.photoId, {
        status: event.operation === 'upload' ? 'uploading' : 'deleting',
      });

      return;
    }

    if (event.type === 'retry_scheduled') {
      this.updatePhoto(event.photoId, {
        status:
          event.operation === 'upload' ? 'queued_upload' : 'queued_remove',
        lastError: event.error,
      });

      return;
    }

    if (event.type === 'failed') {
      this.updatePhoto(event.photoId, {
        status: event.operation === 'upload' ? 'upload_error' : 'remove_error',
        activeTaskId: event.taskId,
        lastError: event.error,
      });

      return;
    }

    if (event.type === 'cancelled') {
      return;
    }

    const fileItem = this.photoFiles().find(file => file.id === event.photoId);

    if (!fileItem) {
      return;
    }

    if (event.result.operation === 'upload') {
      this.updatePhoto(event.photoId, {
        status: 'completed',
        activeTaskId: undefined,
        lastError: undefined,
        createdSubmissionFileId: event.result.result.createdSubmissionFileId,
      });
      this.addUploadedSubmissionFile(fileItem, event.result.result);
    } else {
      this.completeFileRemoval(fileItem);
    }

    try {
      await this.photoQueueService.acknowledgeSuccess(event.taskId);
    } catch (error) {
      console.error(
        '[InputPhotosService] Failed to acknowledge queue success:',
        error,
      );
    }
  }

  private updatePhoto(photoId: string, changes: Partial<FileItem>): void {
    this.photoFiles.update(files =>
      files.map(file => (file.id === photoId ? { ...file, ...changes } : file)),
    );
  }

  private handleQueueStorageFailure(
    photoId: string,
    operation: 'upload' | 'remove',
    error: unknown,
  ): void {
    this.updatePhoto(photoId, {
      status: operation === 'upload' ? 'upload_error' : 'remove_error',
      activeTaskId: undefined,
      lastError: this.createQueueError(
        'queue_storage_error',
        error instanceof Error ? error.message : 'IndexedDB operation failed',
        false,
      ),
    });
    this.showQueueStorageError(error);
  }

  private showQueueStorageError(error: unknown): void {
    this.xAlertService.error(
      this.outkeepTranslateService.instant(
        WorkflowsTranslations.PhotoQueueStorageError,
      ),
      error instanceof Error ? error.message : undefined,
    );
  }

  private createHttpQueueError(error: unknown): PhotoQueueError {
    if (error instanceof HttpErrorResponse) {
      return this.createQueueError(
        `http_${error.status}`,
        error.message,
        false,
        error.status,
      );
    }

    return this.createQueueError(
      'unknown_http_error',
      error instanceof Error ? error.message : 'Unknown HTTP error',
      false,
    );
  }

  private createQueueError(
    code: string,
    message: string,
    retryable: boolean,
    httpStatus?: number,
  ): PhotoQueueError {
    return {
      code,
      message,
      retryable,
      occurredAt: Date.now(),
      httpStatus,
    };
  }

  private isSubmissionFileNotFoundResponse(
    response: SyncWorkflowRemoveFileResponse | null,
  ): boolean {
    return Boolean(
      response &&
      !response.isOk &&
      response.failureReason ===
        SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.submissionFileNotFound,
    );
  }
}
